import React, { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from "react";
import { Box, Grid } from "@mui/material";
import { StriimCheckbox, StriimTypography } from "@striim/striim-ui";

import EllipsisText from "src/generic/ellipsis-text/ellipsis-text";
import SchemasTablesFrame from "../schemas-tables-frame/schemasTablesFrame";
import { SchemasList, SelectTablesHeader, TablesList } from "../index";
import { TablesListHandle } from "../tables-list/tables-list";
import { Schema, Tables } from "../../schema-table";
import { SourceInfo, sourceInfoInit, useSelectTables } from "src/modules/flow-designer/vts/select-tables-context";
import { DB_PROVIDERS } from "app/components/appwizard/component-wizards/wizards/source/database-reader/configs/db-providers";
import { styles } from "../../select-tables-modal.styles";

interface SelectTablesWrapperProps {
    schemas: Schema[];
    setSchemas: Dispatch<SetStateAction<Schema[]>>;
    sourceInfo?: SourceInfo;
    //Compact variant to show up in ai sidebar
    compact?: boolean;
}

const SelectTablesWrapper: React.FC<SelectTablesWrapperProps> = ({
    schemas,
    setSchemas,
    sourceInfo: sourceInfoProp,
    compact = false
}) => {
    const [selectedSchema, setSelectedSchema] = useState<Schema>(schemas[0]);
    const [filteredTables, setFilteredTables] = useState<Tables[] | null>(null);
    const [searchInput, setSearchInput] = useState<string>("");
    const tablesListRef = useRef<TablesListHandle>(null);
    const { sourceInfo: sourceInfoContext } = useSelectTables();
    const { schemaType, entityType, dbType } = sourceInfoProp ?? sourceInfoContext ?? sourceInfoInit;

    const handleSelectAllSchemasAndTables = (value: boolean) => {
        const updatedSchemas = schemas.map(schema => {
            const updatedTables = schema.tables.map(table => ({ ...table, isSelected: value }));
            return { ...schema, isSelected: value, tables: updatedTables };
        });
        const updatedCurrentSelection = updatedSchemas.find(schema => schema.name === selectedSchema.name);
        setSchemas(updatedSchemas);
        setSelectedSchema(updatedCurrentSelection);
    };

    const handleSelectAllTables = (checked: boolean) => {
        const updatedTables = selectedSchema.tables.map(table => {
            if (!filteredTables) {
                return {
                    ...table,
                    isSelected: checked
                };
            }
            const currentTableObj = filteredTables.find(filteredTable => filteredTable.name === table.name);
            if (currentTableObj) {
                return {
                    ...table,
                    isSelected: checked
                };
            }
            return table;
        });

        const isSomeTableSelected = updatedTables.some(table => !!table.isSelected);
        const updatedSchema = {
            ...selectedSchema,
            isSelected: isSomeTableSelected,
            tables: updatedTables
        };
        const updatedSchemas = schemas.map(schema => (schema.name === selectedSchema.name ? updatedSchema : schema));
        setSchemas(updatedSchemas);
        setSelectedSchema(updatedSchema);
    };

    const handleTableSelection = (tableName: string, checked: boolean) => {
        const tablesInSchema = [...selectedSchema.tables];
        const updatedTables = tablesInSchema.map(table =>
            table.name === tableName ? { ...table, isSelected: checked } : table
        );
        const isSomeTableSelected = updatedTables.some(table => !!table.isSelected);
        const updatedSchema = {
            ...selectedSchema,
            isSelected: isSomeTableSelected,
            tables: updatedTables
        };
        const updatedSchemas = schemas.map(schema => (schema.name === selectedSchema.name ? updatedSchema : schema));
        setSchemas(updatedSchemas);
        setSelectedSchema(updatedSchema);
    };

    const tableInfo = useMemo(() => {
        const values = schemas.reduce(
            (accumulated, current: Schema) => {
                const selectedTablesInCurrentSchema = current.tables?.filter(table => table.isSelected);
                accumulated.selectedTables.push(selectedTablesInCurrentSchema);
                accumulated.allTables.push(current.tables);
                return accumulated;
            },
            { allTables: [], selectedTables: [] }
        );
        return { allTables: values.allTables.flat(), selectedTables: values.selectedTables.flat() };
    }, [schemas]);

    const MainSectionTitleComponent = useCallback(() => {
        const currentListOfTables = filteredTables ?? selectedSchema?.tables;
        const allTablesInSchema = selectedSchema?.tables;
        const selectedTablesInSelectedSchema = currentListOfTables?.filter(table => !!table.isSelected)?.length || 0;
        const totalSelectedInSchema = allTablesInSchema?.filter(table => !!table.isSelected)?.length || 0;
        const isChecked =
            selectedTablesInSelectedSchema && selectedTablesInSelectedSchema === currentListOfTables?.length;
        const isIndeterminate =
            selectedTablesInSelectedSchema && selectedTablesInSelectedSchema !== currentListOfTables?.length;

        if (!currentListOfTables?.length) return null;

        return (
            <Box
                display={"flex"}
                alignItems={"center"}
                px={compact ? 1.5 : 2}
                py={compact ? 1 : 1.5}
                sx={styles.childSectionTitleContainer}
            >
                <Grid container gap={1} alignItems={"center"} sx={styles.selectAllContainer}>
                    <StriimCheckbox
                        checked={isChecked}
                        indeterminate={isIndeterminate}
                        onChange={event => {
                            handleSelectAllTables(event.target.checked);
                        }}
                        data-test-id="select-all-tables-checkbox"
                    />
                    {!compact && (
                        <StriimTypography
                            variant="h4"
                            color="primary.700"
                            sx={styles.selectAllPrefix}
                            data-test-id="selected-tables-info"
                        >
                            {`Select ${entityType.toLowerCase()}s in ${schemaType.toLowerCase()}: `}
                        </StriimTypography>
                    )}
                    <EllipsisText text={selectedSchema.name} sx={styles.selectAllLabel} />
                    <StriimTypography
                        variant="h4"
                        color="primary.700"
                        sx={styles.selectAllSuffix}
                        data-test-id="selected-tables-count"
                    >
                        {` (${totalSelectedInSchema}/${allTablesInSchema?.length || 0})`}
                    </StriimTypography>
                </Grid>
            </Box>
        );
    }, [filteredTables, selectedSchema]);

    return (
        <SchemasTablesFrame
            showLeftSidebar={true}
            compact={compact}
            leftSidebarTitle={`Source ${schemaType.toLowerCase()}s`}
            Header={
                <SelectTablesHeader
                    entityType={entityType}
                    selectedCount={tableInfo.selectedTables.length}
                    totalCount={tableInfo.allTables.length}
                    onSelectAll={handleSelectAllSchemasAndTables}
                    searchInput={searchInput}
                    setSearchInput={setSearchInput}
                    selectedSchema={selectedSchema}
                    setFilteredTables={setFilteredTables}
                    currentListOfTables={filteredTables ?? selectedSchema?.tables}
                    onFilterApplied={() => tablesListRef.current?.resetPage?.()}
                    compact={compact}
                />
            }
            LeftSidebar={
                <SchemasList
                    selectedSchema={selectedSchema}
                    setSelectedSchema={setSelectedSchema}
                    schemas={schemas}
                    schemaType={schemaType}
                    setSchemas={setSchemas}
                    isMultiSelectionAllowed={dbType !== DB_PROVIDERS.SALESFORCE.id}
                />
            }
            MainSectionTitle={<MainSectionTitleComponent />}
            schemaType={schemaType}
        >
            <TablesList
                ref={tablesListRef}
                schemaName={selectedSchema?.name}
                tables={filteredTables ?? selectedSchema?.tables}
                onTableSelection={handleTableSelection}
                searchInput={searchInput}
                schemaType={schemaType}
                entityType={entityType}
                compact={compact}
            />
        </SchemasTablesFrame>
    );
};

export default SelectTablesWrapper;
